/**
 * 天地图 REST WebService（逆地理、POI 搜索）
 * 纯解析函数可单测；网络请求走 Vite 代理或直连官方域名。
 */
import { getTiandituApiBase, TIANDITU_TK } from '@/utils/tiandituConfig'

export interface TiandituAddressComponent {
  province?: string
  city?: string
  county?: string
  district?: string
  town?: string
  /** 道路名（天地图 REST 常用 road；部分场景也可能返回 street） */
  street?: string
  road?: string
  streetNumber?: string
  street_number?: string
  address?: string
  poi?: string
}

export interface TiandituReverseGeocodeParsed {
  formattedAddress: string
  addressComponent: TiandituAddressComponent
  longitude?: number
  latitude?: number
}

export interface TiandituPlaceSuggestion {
  id: string
  title: string
  address: string
  location: { lat: number; lng: number }
  province?: string
  city?: string
  county?: string
}

interface TiandituGeocoderResponse {
  status?: string | number
  msg?: string
  result?: {
    formatted_address?: string
    location?: { lon?: number | string; lat?: number | string }
    addressComponent?: TiandituAddressComponent
  }
}

interface TiandituSearchResponse {
  status?: { infocode?: number; cndesc?: string }
  pois?: Array<{
    name?: string
    address?: string
    lonlat?: string
    city?: string
    province?: string
    county?: string
    hotPointID?: string
  }>
}

/** 解析 lonlat 字符串「经度,纬度」 */
export function parseLonLat(lonlat?: string): { lng: number; lat: number } | null {
  if (!lonlat) return null
  const [lonText, latText] = lonlat.split(',')
  const lng = Number(lonText)
  const lat = Number(latText)
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null
  return { lng, lat }
}

/** 解析天地图逆地理响应；失败返回 null */
export function parseTiandituGeocoderResponse(
  data: TiandituGeocoderResponse | null | undefined
): TiandituReverseGeocodeParsed | null {
  if (!data) return null
  const statusOk = data.status === '0' || data.status === 0
  if (!statusOk || !data.result) return null

  const comp = data.result.addressComponent || {}
  const lon = Number(data.result.location?.lon)
  const lat = Number(data.result.location?.lat)

  return {
    formattedAddress: data.result.formatted_address || comp.address || '',
    addressComponent: {
      province: comp.province || '',
      city: comp.city || '',
      county: comp.county || comp.district || '',
      district: comp.district || comp.county || '',
      town: comp.town || '',
      // 天地图逆地理主字段为 road；兼容 street / street_number
      street: comp.street || comp.road || '',
      road: comp.road || comp.street || '',
      streetNumber: comp.streetNumber || comp.street_number || '',
      address: comp.address || '',
      poi: comp.poi || ''
    },
    longitude: Number.isFinite(lon) ? lon : undefined,
    latitude: Number.isFinite(lat) ? lat : undefined
  }
}

/** 将天地图 POI 列表映射为 SelectMap 可用结构 */
export function mapTiandituPoisToSuggestions(
  pois: TiandituSearchResponse['pois']
): TiandituPlaceSuggestion[] {
  if (!Array.isArray(pois)) return []
  return pois
    .map((item, index) => {
      const coords = parseLonLat(item.lonlat)
      if (!coords || !item.name) return null
      return {
        id: item.hotPointID || `${coords.lng},${coords.lat},${index}`,
        title: item.name,
        address: item.address || item.name,
        location: { lat: coords.lat, lng: coords.lng },
        province: item.province,
        city: item.city,
        county: item.county
      } satisfies TiandituPlaceSuggestion
    })
    .filter(Boolean) as TiandituPlaceSuggestion[]
}

/** 从逆地理结果拼街道名（优先道路+门牌，其次乡镇/街道办） */
export function buildStreetNameFromComponent(comp: TiandituAddressComponent): string {
  const road = comp.street || comp.road || ''
  const number = comp.streetNumber || comp.street_number || ''
  const roadLine = [road, number].filter(Boolean).join('')
  if (roadLine) return roadLine
  return (comp.town || '').trim()
}

async function tdtGet<T>(path: string, queryParams: Record<string, string>): Promise<T> {
  const query = Object.entries({ ...queryParams, tk: TIANDITU_TK })
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&')
  const url = `${getTiandituApiBase()}${path}?${query}`
  const res = await fetch(url)
  if (!res.ok) {
    throw new Error(`天地图请求失败(${res.status})`)
  }
  return (await res.json()) as T
}

/** 逆地理：经纬度 → 结构化地址（WGS84） */
export async function reverseGeocodeByLngLat(
  lng: number,
  lat: number
): Promise<TiandituReverseGeocodeParsed | null> {
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null
  const postStr = JSON.stringify({ lon: lng, lat, ver: 1 })
  const data = await tdtGet<TiandituGeocoderResponse>('/geocoder', {
    postStr,
    type: 'geocode'
  })
  return parseTiandituGeocoderResponse(data)
}

/**
 * 关键词地点搜索（行政区域检索）
 * @param keyword 关键词
 * @param specify 指定行政区，如「成都市」
 */
export async function searchPlaceSuggestion(
  keyword: string,
  specify = '成都市'
): Promise<TiandituPlaceSuggestion[]> {
  const key = keyword.trim()
  if (key.length < 2) return []

  const postStr = JSON.stringify({
    keyWord: key,
    level: 12,
    mapBound: '-180,-90,180,90',
    queryType: 12,
    start: 0,
    count: 20,
    specify: specify || '成都市'
  })

  const data = await tdtGet<TiandituSearchResponse>('/v2/search', {
    postStr,
    type: 'query'
  })

  if (data?.status?.infocode != null && data.status.infocode !== 1000) {
    throw new Error(data.status.cndesc || '天地图地点搜索失败')
  }
  return mapTiandituPoisToSuggestions(data?.pois)
}

/**
 * 优先用 JS API Geocoder（无 CORS）；不可用时再走 REST
 * 供地图组件点击选点解析地址
 */
export function reverseGeocodeViaJsApi(
  lng: number,
  lat: number
): Promise<TiandituReverseGeocodeParsed | null> {
  const T = (window as any).T
  if (!T?.Geocoder || !T?.LngLat) {
    return reverseGeocodeByLngLat(lng, lat)
  }

  return new Promise((resolve) => {
    try {
      const geocoder = new T.Geocoder()
      geocoder.getLocation(new T.LngLat(lng, lat), (result: any) => {
        if (!result) {
          resolve(null)
          return
        }
        const comp = result.addressComponent || {}
        resolve({
          formattedAddress:
            result.getAddress?.() ||
            result.formatted_address ||
            comp.address ||
            '',
          addressComponent: {
            province: comp.province || '',
            city: comp.city || '',
            county: comp.county || comp.district || '',
            district: comp.district || comp.county || '',
            town: comp.town || '',
            street: comp.street || comp.road || '',
            road: comp.road || comp.street || '',
            streetNumber: comp.streetNumber || comp.street_number || '',
            address: comp.address || '',
            poi: comp.poi || ''
          },
          longitude: lng,
          latitude: lat
        })
      })
    } catch {
      resolve(null)
    }
  })
}
